import React from "react";
import { Card, CardBody } from "reactstrap";
import { getStreamBattle } from "../../helpers/api/battle";

interface ArenaProps {
  selectedBattle: any | null; // idéalement typer : { id: string; status: string; startTime: string; ... }
  variant?: "card" | "slot";
}

const NOT_STARTED_POLL_INTERVAL_MS = 1000;
const NOT_STARTED_MAX_WAIT_MS = 2 * 60 * 1000;

function extractApiMessage(err: any): string {
  // Axios: err.response.data.message
  // Fetch wrapper: à adapter si tu as un autre format
  const m = err?.response?.data?.message ?? err?.message;
  if (Array.isArray(m)) return m.join(", ");
  return typeof m === "string" ? m : "Unknown error";
}

export default function Arena({ selectedBattle, variant = "card" }: ArenaProps) {
  const [playbackId, setPlaybackId] = React.useState<string | null>(null);
  const [useControllerVideo, setUseControllerVideo] = React.useState<boolean>(false);
  const [statusText, setStatusText] = React.useState<string>("");
  const selectedBattleId = selectedBattle?.id;
  const selectedBattleStartTime = selectedBattle?.startTime;
  const selectedBattleDurationSec = selectedBattle?.durationSec;

  // force le reload de l'iframe quand on récupère enfin le playbackId
  const iframeKey = React.useMemo(
    () => `${playbackId ?? "none"}-${useControllerVideo ? "controls" : "no-controls"}`,
    [playbackId, useControllerVideo]
  );

  React.useEffect(() => {
    let startTimeoutId: number | undefined;
    let pollIntervalId: number | undefined;
    let controlsTimeoutId: number | undefined;
    let cancelled = false;
    let notStartedDeadline: number | undefined;

    const clearStartAndPollTimers = () => {
      if (startTimeoutId) window.clearTimeout(startTimeoutId);
      if (pollIntervalId) window.clearInterval(pollIntervalId);
      startTimeoutId = undefined;
      pollIntervalId = undefined;
      notStartedDeadline = undefined;
    };

    const clearAllTimers = () => {
      clearStartAndPollTimers();
      if (controlsTimeoutId) window.clearTimeout(controlsTimeoutId);
      controlsTimeoutId = undefined;
    };

    const getStartMs = () => {
      // IMPORTANT : ici on dépend de selectedBattle.startTime
      // Si tu ne l’as pas dans selectedBattle, il faut l’ajouter côté API "list battles".
      return selectedBattleStartTime ? new Date(selectedBattleStartTime).getTime() : NaN;
    };

    const getEndMs = () => {
      const startMs = getStartMs();
      if (!Number.isFinite(startMs)) return NaN;
      return startMs + Number(selectedBattleDurationSec || 0) * 1000;
    };

    const areControlsAvailable = () => {
      const endMs = getEndMs();
      return Number.isFinite(endMs) && Date.now() > endMs;
    };

    const scheduleControlsAvailability = () => {
      if (controlsTimeoutId) window.clearTimeout(controlsTimeoutId);

      const endMs = getEndMs();
      if (!Number.isFinite(endMs)) return;

      const delay = endMs - Date.now() + 1;
      if (delay <= 0) {
        setUseControllerVideo(true);
        return;
      }

      controlsTimeoutId = window.setTimeout(() => {
        if (!cancelled) setUseControllerVideo(true);
      }, delay);
    };

    const scheduleAtStartTime = () => {
      const startMs = getStartMs();

      if (!Number.isFinite(startMs)) {
        setStatusText("Start time is missing for this battle.");
        return;
      }

      const now = Date.now();
      if (now >= startMs) return;

      setStatusText("Battle selected. Waiting for start time…");
      setPlaybackId(null);

      const delay = Math.max(0, startMs - now);
      startTimeoutId = window.setTimeout(() => {
        if (!cancelled) tryFetchStreamOnceThenMaybePoll();
      }, delay);
    };

    const tryFetchStreamOnceThenMaybePoll = async () => {
      // Selon ton back : si status ACTIVE => 403 "Battle is active"
      // Tu peux choisir de ne même pas appeler l’API dans ce cas.
      setStatusText("Checking stream availability…");
      const start = getStartMs();
      const now = Date.now();

      if (!Number.isFinite(start)) {
        setStatusText("Start time is missing for this battle.");
        clearStartAndPollTimers();
        return;
      }

      if (start > now) {
        // Avant T0: ne pas appeler l’API, replanifier à T0
        setPlaybackId(null);
        setStatusText("Waiting for start time…");
        clearStartAndPollTimers();
        const delay = Math.max(0, start - now);
        startTimeoutId = window.setTimeout(() => {
          if (!cancelled) tryFetchStreamOnceThenMaybePoll();
        }, delay);
        return;
      }

      try {
        const data = await getStreamBattle(selectedBattleId);
        console.log("Battle stream data:", data);
        if (cancelled) return;

        setUseControllerVideo(areControlsAvailable());
        setPlaybackId(data.streamPlaybackId);
        setStatusText("");
        clearStartAndPollTimers();
        scheduleControlsAvailability();
      } catch (err) {
        if (cancelled) return;

        const msg = extractApiMessage(err);

        if (msg.includes("Battle not started yet")) {
          // Après T0 mais backend pas encore prêt: démarrer un polling borné
          setPlaybackId(null);
          setStatusText("Start time reached. Waiting for stream to start…");
          if (!pollIntervalId) {

            notStartedDeadline = Date.now() + NOT_STARTED_MAX_WAIT_MS;
            pollIntervalId = window.setInterval(() => {
              if (cancelled) return;
              if (notStartedDeadline && Date.now() > notStartedDeadline) {
                clearStartAndPollTimers();
                setStatusText("Stream still not available. Please retry later.");
                return;
              }
              tryFetchStreamOnceThenMaybePoll();
            }, NOT_STARTED_POLL_INTERVAL_MS);
          }
          return;
        }

        if (msg.includes("Video not ready")) {
          // on poll jusqu’à ce que le back renvoie 200
          setPlaybackId(null);
          setStatusText("Start time reached. Video is processing…");

          if (!pollIntervalId) {
            pollIntervalId = window.setInterval(() => {
              if (!cancelled) tryFetchStreamOnceThenMaybePoll();
            }, NOT_STARTED_POLL_INTERVAL_MS);
          }
          return;
        }

        if (msg.includes("Battle is active")) {
          setPlaybackId(null);
          setStatusText("Battle is active. No replay/stream available.");
          clearStartAndPollTimers();
          return;
        }

        // autres erreurs
        setPlaybackId(null);
        setStatusText(`Failed to load stream: ${msg}`);
        clearStartAndPollTimers();
      }
    };

    // reset à chaque changement de battle
    clearAllTimers();
    setPlaybackId(null);
    setUseControllerVideo(false);
    setStatusText("");

    if (!selectedBattleId) return;

    // Si on est avant startTime, on attend sans spammer le back
    scheduleAtStartTime();

    // Sinon on tente tout de suite, et on poll si "Video not ready"
    tryFetchStreamOnceThenMaybePoll();

    return () => {
      cancelled = true;
      clearAllTimers();
    };
  }, [selectedBattleDurationSec, selectedBattleId, selectedBattleStartTime]);

  const content = !selectedBattle ? (
    <div className="text-muted text-center">
      Select a battle from the list to display it here.
    </div>
  ) : (
    <div
      className="w-100 h-100 d-flex flex-column justify-content-center align-items-center"
      data-cy={variant === "slot" ? "battle-video-slot" : undefined}
    >
      {statusText && !playbackId ? <div className="text-muted text-center px-3">{statusText}</div> : null}

      {playbackId ? (
        <div className="position-relative w-100 h-100">
          <iframe
            key={iframeKey}
            title={`battle-video-${iframeKey}`}
            data-cy="battle-video-player"
            src={`https://customer-rsd64dh1jy20c3rc.cloudflarestream.com/${playbackId}/iframe?autoplay=true${
              useControllerVideo ? "" : "&controls=false"
            }`}
            loading="lazy"
            style={{
              border: "none",
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: "100%",
            }}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
          />
        </div>
      ) : null}
    </div>
  );

  if (variant === "slot") {
    return content;
  }

  return (
    <React.Fragment>
      <Card>
        <div className="card-header align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">Live Battle</h4>
        </div>

        <CardBody
          className="d-flex flex-column justify-content-center align-items-center gap-3"
          style={{ minHeight: "calc(100vh - 230px)" }}
        >
          {content}
        </CardBody>
      </Card>
    </React.Fragment>
  );
}
